import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Progress,
  Nav,
  NavItem,
  NavLink,
  UncontrolledTooltip
} from 'reactstrap';
import Swal from 'sweetalert2';
import { fetchTests, fetchTest, deleteTest, updateTest } from '../../../redux/QuestionBank/TestProgressAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../../redux/QuestionBank/QuestionsAction';
import { getDisplayName } from 'util/specialtyDisplayHelper';
import Header from 'components/Headers/Header';
import AccessRestriction from '../../../components/AccessRestriction/AccessRestriction';
import TrialBanner from '../../../components/Trial/TrialBanner';
import BoardsCountdownBanner from '../../../components/Trial/BoardsCountdownBanner';
import AddonCrossSellBanner from '../../../components/Billing/AddonCrossSellBanner';
import MissedQuestionsPanel from '../../../components/QuestionBank/MissedQuestionsPanel';

const PreviousTests = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { tests, loading } = useSelector(state => state.testProgress);
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { questions } = useSelector(state => state.questionBankQuestions);
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);
  // 'tests' keeps the existing view as the default; 'missed' mounts lazily
  const [activeTab, setActiveTab] = useState('tests');
  const [selectedTest, setSelectedTest] = useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [resultsData, setResultsData] = useState([]);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingTestId, setEditingTestId] = useState(null);
  const [editingTestName, setEditingTestName] = useState('');
  const [hoveredRowTestId, setHoveredRowTestId] = useState(null);
  const [isEditModeModalOpen, setIsEditModeModalOpen] = useState(false);
  const [editingModeTest, setEditingModeTest] = useState(null);
  const [editModeTimed, setEditModeTimed] = useState(false);
  const [editModeTutor, setEditModeTutor] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'testNumber', direction: 'desc' });

  // Build lookup map for O(1) question resolution instead of O(n) per question
  const questionsMap = useMemo(() => {
    const map = {};
    questions?.forEach(q => { map[q.questionId] = q; });
    return map;
  }, [questions]);

  useEffect(() => {
    if (userHandle) {
      dispatch(fetchTests({ pageSize: 5000 }));
    }
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions());
  }, [dispatch, location.key, userHandle, activeSpecialty]);

  // Auto-open analysis modal when redirected from test completion
  useEffect(() => {
    const showAnalysisTestId = location.state?.showAnalysis;
    if (showAnalysisTestId && tests?.length > 0 && !loading) {
      const test = tests.find(t => t.testId === showAnalysisTestId);
      if (test) {
        handleViewTestAnalysis(test);
        // Clear the state so it doesn't re-trigger on navigation
        history.replace(location.pathname, {});
      }
    }
  }, [location.state?.showAnalysis, tests, loading]);

  const handleContinueTest = (test) => {
    // Navigate to CreateTest page with test ID
    history.push('/client/question-bank/create-test', { resumeTestId: test.testId });
  };

  const handleReviewTest = (test) => {
    // Navigate to CreateTest page in review mode
    history.push('/client/question-bank/create-test', {
      resumeTestId: test.testId,
      reviewMode: true
    });
  };

  const handleDeleteTest = (testId, testName) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${testName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteTest(testId));
      }
    });
  };

  const handleViewTestResults = async (test) => {
    // Fetch full test data on demand if lightweight (no testQuestions)
    let fullTest = test;
    if (!test.testQuestions) {
      const result = await dispatch(fetchTest(test.testId));
      if (result.success) {
        fullTest = result.test;
      }
    }
    setSelectedTest(fullTest);
    processResultsData(fullTest);
    setIsResultsModalOpen(true);
  };

  const processResultsData = (test) => {
    if (!test.testQuestions) {
      setResultsData([]);
      return;
    }

    const results = test.testQuestions.map((question, index) => {
      const selectedAnswerId = test.selectedAnswers?.[question.questionId];
      const correctAnswer = question.answers?.find(a => a.isCorrect);
      const isCorrect = selectedAnswerId === correctAnswer?.answerId;
      const wasOmitted = !selectedAnswerId;

      // Get fresh question analytics from Redux store (tracks all users' answers)
      const freshQuestion = questionsMap[question.questionId];
      const timesAnswered = freshQuestion?.timesAnswered || question.timesAnswered || 0;
      const timesCorrect = freshQuestion?.timesCorrect || question.timesCorrect || 0;
      const averageTimeSpent = freshQuestion?.averageTimeSpent || question.averageTimeSpent || 0;

      // Calculate % correct by others (across all users who answered this question)
      const percentCorrectOthers = timesAnswered > 0
        ? Math.round((timesCorrect / timesAnswered) * 100)
        : 0;

      // Get time spent on this question by current user
      const timeSpent = test.questionTimeTracking?.[question.questionId] || 0;

      // Look up subject, unit, and topic names from IDs with code prefix
      const subjectObj = subjects?.find(s => s.subjectId === question.subjectId);
      const subjectName = subjectObj ? getDisplayName(subjectObj, activeSpecialty) : '-';
      const unitObj = units?.find(u => u.unitId === question.unitId);
      const unitName = unitObj ? getDisplayName(unitObj, activeSpecialty) : '-';
      const topicObj = topics?.find(t => t.topicId === question.topicId);
      const topicName = topicObj ? getDisplayName(topicObj, activeSpecialty) : '-';

      return {
        index: index + 1,
        questionId: question.questionId,
        questionNumber: question.questionNumber || question.questionCode,
        subject: subjectName,
        unit: unitName,
        topic: topicName,
        status: wasOmitted ? 'Omitted' : (isCorrect ? 'Correct' : 'Incorrect'),
        percentCorrectOthers,
        timeSpent,
        averageTimeSpent
      };
    });

    setResultsData(results);
  };

  const handleViewTestAnalysis = async (test) => {
    // Fetch full test data on demand if lightweight (no testQuestions)
    let fullTest = test;
    if (!test.testQuestions) {
      const result = await dispatch(fetchTest(test.testId));
      if (result.success) {
        fullTest = result.test;
      }
    }
    setSelectedTest(fullTest);
    processAnalysisData(fullTest);
    setIsAnalysisModalOpen(true);
  };

  const processAnalysisData = (test) => {
    if (!test.testQuestions) {
      setAnalysisData(null);
      return;
    }

    // Process each question for analysis
    const questionAnalysis = test.testQuestions.map((question, index) => {
      const selectedAnswerId = test.selectedAnswers?.[question.questionId];
      const correctAnswer = question.answers?.find(a => a.isCorrect);
      const isCorrect = selectedAnswerId === correctAnswer?.answerId;
      const wasOmitted = !selectedAnswerId;
      const timeSpent = test.questionTimeTracking?.[question.questionId] || 0;

      // Get fresh question analytics
      const freshQuestion = questionsMap[question.questionId];
      const timesAnswered = freshQuestion?.timesAnswered || question.timesAnswered || 0;
      const timesCorrect = freshQuestion?.timesCorrect || question.timesCorrect || 0;
      const percentCorrectOthers = timesAnswered > 0 ? Math.round((timesCorrect / timesAnswered) * 100) : 50;

      const subjectObj = subjects?.find(s => s.subjectId === question.subjectId);
      const subjectName = subjectObj ? getDisplayName(subjectObj, activeSpecialty) : 'Unknown';
      const unitObj = units?.find(u => u.unitId === question.unitId);
      const unitName = unitObj ? getDisplayName(unitObj, activeSpecialty) : 'Unknown';
      const topicObj = topics?.find(t => t.topicId === question.topicId);
      const topicName = topicObj ? getDisplayName(topicObj, activeSpecialty) : 'Unknown';

      return {
        questionId: question.questionId,
        subject: subjectName,
        subjectId: question.subjectId,
        unit: unitName,
        unitId: question.unitId,
        topic: topicName,
        topicId: question.topicId,
        isCorrect,
        wasOmitted,
        timeSpent,
        difficulty: percentCorrectOthers
      };
    });

    // Performance by Subject
    const subjectStats = {};
    questionAnalysis.forEach(q => {
      if (!subjectStats[q.subject]) {
        subjectStats[q.subject] = { total: 0, correct: 0, omitted: 0, totalTime: 0 };
      }
      subjectStats[q.subject].total++;
      if (q.isCorrect) subjectStats[q.subject].correct++;
      if (q.wasOmitted) subjectStats[q.subject].omitted++;
      subjectStats[q.subject].totalTime += q.timeSpent;
    });

    const subjectPerformance = Object.entries(subjectStats).map(([name, stats]) => ({
      name,
      total: stats.total,
      correct: stats.correct,
      incorrect: stats.total - stats.correct - stats.omitted,
      omitted: stats.omitted,
      percentage: Math.round((stats.correct / stats.total) * 100),
      avgTime: stats.total > 0 ? Math.round(stats.totalTime / stats.total) : 0
    })).sort((a, b) => b.total - a.total);

    // Performance by Unit
    const unitStats = {};
    questionAnalysis.forEach(q => {
      const key = `${q.subject} - ${q.unit}`;
      if (!unitStats[key]) {
        unitStats[key] = { subject: q.subject, unit: q.unit, total: 0, correct: 0, omitted: 0, totalTime: 0 };
      }
      unitStats[key].total++;
      if (q.isCorrect) unitStats[key].correct++;
      if (q.wasOmitted) unitStats[key].omitted++;
      unitStats[key].totalTime += q.timeSpent;
    });

    const unitPerformance = Object.entries(unitStats).map(([key, stats]) => ({
      name: key,
      subject: stats.subject,
      unit: stats.unit,
      total: stats.total,
      correct: stats.correct,
      incorrect: stats.total - stats.correct - stats.omitted,
      omitted: stats.omitted,
      percentage: Math.round((stats.correct / stats.total) * 100),
      avgTime: stats.total > 0 ? Math.round(stats.totalTime / stats.total) : 0
    })).sort((a, b) => b.total - a.total);

    // Performance by Topic
    const topicStats = {};
    questionAnalysis.forEach(q => {
      if (!q.topic || q.topic === 'Unknown') return;
      const key = `${q.unit} - ${q.topic}`;
      if (!topicStats[key]) {
        topicStats[key] = { subject: q.subject, unit: q.unit, topic: q.topic, total: 0, correct: 0, omitted: 0, totalTime: 0 };
      }
      topicStats[key].total++;
      if (q.isCorrect) topicStats[key].correct++;
      if (q.wasOmitted) topicStats[key].omitted++;
      topicStats[key].totalTime += q.timeSpent;
    });

    const topicPerformance = Object.entries(topicStats).map(([key, stats]) => ({
      name: key,
      subject: stats.subject,
      unit: stats.unit,
      topic: stats.topic,
      total: stats.total,
      correct: stats.correct,
      incorrect: stats.total - stats.correct - stats.omitted,
      omitted: stats.omitted,
      percentage: Math.round((stats.correct / stats.total) * 100),
      avgTime: stats.total > 0 ? Math.round(stats.totalTime / stats.total) : 0
    })).sort((a, b) => b.total - a.total);

    // Time Analysis
    const totalTimeSpent = questionAnalysis.reduce((sum, q) => sum + q.timeSpent, 0);
    const avgTimePerQuestion = questionAnalysis.length > 0 ? Math.round(totalTimeSpent / questionAnalysis.length) : 0;
    const correctQuestions = questionAnalysis.filter(q => q.isCorrect);
    const incorrectQuestions = questionAnalysis.filter(q => !q.isCorrect && !q.wasOmitted);
    const avgTimeCorrect = correctQuestions.length > 0
      ? Math.round(correctQuestions.reduce((sum, q) => sum + q.timeSpent, 0) / correctQuestions.length)
      : 0;
    const avgTimeIncorrect = incorrectQuestions.length > 0
      ? Math.round(incorrectQuestions.reduce((sum, q) => sum + q.timeSpent, 0) / incorrectQuestions.length)
      : 0;

    // Difficulty Analysis
    const easyQuestions = questionAnalysis.filter(q => q.difficulty >= 70);
    const mediumQuestions = questionAnalysis.filter(q => q.difficulty >= 40 && q.difficulty < 70);
    const hardQuestions = questionAnalysis.filter(q => q.difficulty < 40);

    const difficultyBreakdown = {
      easy: {
        total: easyQuestions.length,
        correct: easyQuestions.filter(q => q.isCorrect).length,
        percentage: easyQuestions.length > 0
          ? Math.round((easyQuestions.filter(q => q.isCorrect).length / easyQuestions.length) * 100)
          : 0
      },
      medium: {
        total: mediumQuestions.length,
        correct: mediumQuestions.filter(q => q.isCorrect).length,
        percentage: mediumQuestions.length > 0
          ? Math.round((mediumQuestions.filter(q => q.isCorrect).length / mediumQuestions.length) * 100)
          : 0
      },
      hard: {
        total: hardQuestions.length,
        correct: hardQuestions.filter(q => q.isCorrect).length,
        percentage: hardQuestions.length > 0
          ? Math.round((hardQuestions.filter(q => q.isCorrect).length / hardQuestions.length) * 100)
          : 0
      }
    };

    // Identify Strengths and Weaknesses (topics with 1+ questions, or units with 2+ questions if no topics)
    const significantTopics = topicPerformance.filter(t => t.total >= 1);
    const significantUnits = unitPerformance.filter(u => u.total >= 2);

    // Prefer topics for strengths/weaknesses, fall back to units if no topics
    const strengthsSource = significantTopics.length > 0 ? significantTopics : significantUnits;
    const weaknessesSource = significantTopics.length > 0 ? significantTopics : significantUnits;

    const strengths = strengthsSource.filter(item => item.percentage >= 70).sort((a, b) => b.percentage - a.percentage).slice(0, 3);
    const weaknesses = weaknessesSource.filter(item => item.percentage < 70).sort((a, b) => a.percentage - b.percentage).slice(0, 3);

    // Overall stats
    const totalQuestions = questionAnalysis.length;
    const totalCorrect = questionAnalysis.filter(q => q.isCorrect).length;
    const totalIncorrect = questionAnalysis.filter(q => !q.isCorrect && !q.wasOmitted).length;
    const totalOmitted = questionAnalysis.filter(q => q.wasOmitted).length;

    setAnalysisData({
      questionAnalysis,
      subjectPerformance,
      unitPerformance,
      topicPerformance,
      timeAnalysis: {
        totalTimeSpent,
        avgTimePerQuestion,
        avgTimeCorrect,
        avgTimeIncorrect
      },
      difficultyBreakdown,
      strengths,
      weaknesses,
      overall: {
        total: totalQuestions,
        correct: totalCorrect,
        incorrect: totalIncorrect,
        omitted: totalOmitted,
        score: test.score || 0
      }
    });
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getResultStatusBadge = (status) => {
    switch (status) {
      case 'Correct':
        return <Badge color="success">Correct</Badge>;
      case 'Incorrect':
        return <Badge color="danger">Incorrect</Badge>;
      case 'Omitted':
        return <Badge color="warning">Omitted</Badge>;
      default:
        return <Badge color="secondary">{status}</Badge>;
    }
  };

  const getModeDisplay = (test) => {
    if (!test) return '';
    const tutor = test.isTutor ? 'Tutored' : 'Untutored';
    const timed = test.isTimed ? 'Timed' : 'Untimed';
    return `${tutor}, ${timed}`;
  };

  const getQuestionPoolDisplay = (test) => {
    if (!test) return '';
    const mode = test.selectionMode || 'Standard';
    return mode.charAt(0).toUpperCase() + mode.slice(1);
  };

  // Calculate average difficulty - the average % of users who got each question correct
  const getAverageDifficulty = () => {
    if (!resultsData.length) return 0;
    const avgCorrect = resultsData.reduce((sum, r) => sum + r.percentCorrectOthers, 0) / resultsData.length;
    return Math.round(avgCorrect);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  // Search and filter logic
  const filteredTests = tests?.filter(test => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const testNumber = (test.testNumber || '').toString();
    const testName = (test.testName || '').toLowerCase();
    const testSubjects = (test.subjects || []).join(' ').toLowerCase();
    const testUnits = (test.units || []).join(' ').toLowerCase();
    const testTopics = (test.topics || []).join(' ').toLowerCase();
    const mode = `${test.isTimed ? 'timed' : 'untimed'} ${test.isTutor ? 'tutored' : 'untutored'}`.toLowerCase();
    const selectionMode = (test.selectionMode || '').toLowerCase();
    const totalQuestions = (test.totalQuestions || 0).toString();

    return testNumber.includes(query) ||
           testName.includes(query) ||
           testSubjects.includes(query) ||
           testUnits.includes(query) ||
           testTopics.includes(query) ||
           mode.includes(query) ||
           selectionMode.includes(query) ||
           totalQuestions.includes(query);
  }) || [];

  // Sorting logic
  const sortedTests = [...filteredTests].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle special cases
    if (sortConfig.key === 'testNumber') {
      // For testNumber, treat undefined/null as infinity to push them to the end
      aValue = a.testNumber ?? Infinity;
      bValue = b.testNumber ?? Infinity;
    } else if (sortConfig.key === 'score') {
      aValue = a.status === 'completed' && a.score !== null ? a.score : -1;
      bValue = b.status === 'completed' && b.score !== null ? b.score : -1;
    } else if (sortConfig.key === 'mode') {
      aValue = `${a.isTimed ? 'timed' : 'untimed'} ${a.isTutor ? 'tutored' : 'untutored'}`;
      bValue = `${b.isTimed ? 'timed' : 'untimed'} ${b.isTutor ? 'tutored' : 'untutored'}`;
    } else if (sortConfig.key === 'subjects') {
      aValue = (a.subjects || []).join(', ');
      bValue = (b.subjects || []).join(', ');
    } else if (sortConfig.key === 'units') {
      aValue = (a.units || []).join(', ');
      bValue = (b.units || []).join(', ');
    } else if (sortConfig.key === 'topics') {
      aValue = (a.topics || []).join(', ');
      bValue = (b.topics || []).join(', ');
    }

    // Convert to string for comparison if needed
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTests = sortedTests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedTests.length / itemsPerPage);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="fas fa-sort text-muted ml-1" style={{ fontSize: '0.75rem' }}></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up text-primary ml-1" style={{ fontSize: '0.75rem' }}></i>
      : <i className="fas fa-sort-down text-primary ml-1" style={{ fontSize: '0.75rem' }}></i>;
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleStartEditName = (test) => {
    setEditingTestId(test.testId);
    setEditingTestName(test.testName || '');
  };

  const handleCancelEditName = () => {
    setEditingTestId(null);
    setEditingTestName('');
  };

  const handleSaveTestName = async (testId) => {
    if (editingTestName.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Name',
        text: 'Test name cannot be empty',
        confirmButtonColor: '#5e72e4'
      });
      return;
    }

    const result = await dispatch(updateTest(testId, { testName: editingTestName.trim() }));

    if (result.success) {
      await dispatch(fetchTests({ pageSize: 5000 }));
      setEditingTestId(null);
      setEditingTestName('');
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Test name has been updated',
        confirmButtonColor: '#5e72e4',
        timer: 1500,
        showConfirmButton: false
      });
    }
  };

  const handleOpenEditMode = (test) => {
    setEditingModeTest(test);
    setEditModeTimed(test.isTimed);
    setEditModeTutor(test.isTutor);
    setIsEditModeModalOpen(true);
  };

  const handleCloseEditMode = () => {
    setIsEditModeModalOpen(false);
    setEditingModeTest(null);
  };

  const handleSaveTestMode = async () => {
    const result = await dispatch(updateTest(editingModeTest.testId, {
      isTimed: editModeTimed,
      isTutor: editModeTutor
    }));

    if (result.success) {
      await dispatch(fetchTests({ pageSize: 5000 }));
      setIsEditModeModalOpen(false);
      setEditingModeTest(null);
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Test mode has been updated',
        confirmButtonColor: '#5e72e4',
        timer: 1500,
        showConfirmButton: false
      });
    }
  };

  return (
    <>
      <Header />
      <AccessRestriction>
      <div className="container-fluid mt--7">
        <TrialBanner />
        <BoardsCountdownBanner />
        <AddonCrossSellBanner />
        <Row>
          <Col lg="12">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center ">
                  <Col>
                    <h2 className="mb-0">
                      <i className="fas fa-history mr-2"></i>
                      Previous Tests
                    </h2>
                  </Col>
                  <Col className="text-right">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => history.push('/client/question-bank/create-test')}
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Create New Test
                    </Button>
                  </Col>
                </Row>
                
                <Nav tabs className="mt-3 border-0">
                  <NavItem>
                    <NavLink
                      className={activeTab === 'tests' ? 'active' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActiveTab('tests')}
                    >
                      <i className="fas fa-list mr-2"></i>
                      My Tests
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'missed' ? 'active' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActiveTab('missed')}
                    >
                      <i className="fas fa-times-circle mr-2"></i>
                      Missed Questions
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>

              {activeTab === 'missed' && (
                <CardBody>
                  <MissedQuestionsPanel />
                </CardBody>
              )}

              {/* Hidden rather than unmounted so the existing tests view keeps
                  its state, pagination and fetches exactly as before */}
              <CardBody style={{ display: activeTab === 'tests' ? undefined : 'none' }}>
                {tests && tests.length > 0 && (
                  <Row className='mb-3'>
                    <Col md="6" lg="5">
                      <div className="input-group ">
                        <div className="input-group-prepend">
                          <span className="input-group-text bg-white border-right-0">
                            <i className="fas fa-search text-muted"></i>
                          </span>
                        </div>
                        <Input
                          type="text"
                          placeholder="Search by test #, name, subject, unit, topic, or mode..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1); // Reset to first page when searching
                          }}
                          className="border-left-0"
                          style={{ paddingLeft: '0' }}
                        />
                        {searchQuery && (
                          <div className="input-group-append">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              type="button"
                              onClick={() => setSearchQuery('')}
                              title="Clear search"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
                {loading ? (
                  <div className="text-center py-5">
                    <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Loading tests...</p>
                  </div>
                ) : !tests || tests.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-clipboard-list fa-4x text-muted mb-3"></i>
                    <h3 className="text-muted">No Tests Yet</h3>
                    <p className="text-muted mb-4">
                      Start creating tests to track your progress and performance.
                    </p>
                    <Button
                      color="primary"
                      onClick={() => history.push('/client/question-bank/create-test')}
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Create Your First Test
                    </Button>
                  </div>
                ) : sortedTests.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-search fa-4x text-muted mb-3"></i>
                    <h3 className="text-muted">No Results Found</h3>
                    <p className="text-muted mb-4">
                      No tests match your search criteria. Try a different search term.
                    </p>
                    <Button
                      color="secondary"
                      onClick={() => setSearchQuery('')}
                    >
                      <i className="fas fa-times mr-2"></i>
                      Clear Search
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-items-center table-flush" hover>
                      <thead className="thead-light">
                        <tr>
                          <th>Actions</th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('score')}
                          >
                            Score {getSortIcon('score')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('testNumber')}
                          >
                            Test ID {getSortIcon('testNumber')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('testName')}
                          >
                            Name {getSortIcon('testName')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('createdAt')}
                          >
                            Date {getSortIcon('createdAt')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('mode')}
                          >
                            Mode {getSortIcon('mode')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('selectionMode')}
                          >
                            Q. Pool {getSortIcon('selectionMode')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('subjects')}
                          >
                            Subjects {getSortIcon('subjects')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('units')}
                          >
                            Units {getSortIcon('units')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('topics')}
                          >
                            Topics {getSortIcon('topics')}
                          </th>
                          <th
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('totalQuestions')}
                          >
                            # QS {getSortIcon('totalQuestions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTests.map((test) => (
                          <tr
                            key={test.testId}
                            onMouseEnter={() => setHoveredRowTestId(test.testId)}
                            onMouseLeave={() => setHoveredRowTestId(null)}
                          >
                            <td>
                              <div className="btn-group btn-group-sm" role="group">
                                {test.status === 'in_progress' || test.status === 'paused' ? (
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => handleContinueTest(test)}
                                    title="Continue Test"
                                  >
                                    <i className="fas fa-play"></i>
                                  </Button>
                                ) : (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => handleReviewTest(test)}
                                    title="Review Test"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Button>
                                )}
                                {test.status === 'completed' && (
                                  <>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleViewTestResults(test)}
                                      title="Test Results"
                                    >
                                      <i className="fas fa-chart-bar"></i>
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => handleViewTestAnalysis(test)}
                                      title="Test Analysis"
                                    >
                                      <i className="fas fa-chart-pie"></i>
                                    </Button>
                                  </>
                                )}
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteTest(test.testId, test.testName)}
                                  title="Delete Test"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </div>
                            </td>
                            <td>
                              {test.status === 'completed' && test.score != null && test.score !== undefined ? (
                                <span
                                  className={`font-weight-bold ${test.score >= 70 ? 'text-success' : test.score >= 50 ? 'text-warning' : 'text-danger'}`}
                                  style={{ fontSize: '1rem' }}
                                >
                                  {test.score}%
                                </span>
                              ) : test.status === 'in_progress' || test.status === 'paused' ? (
                                <Badge color="info" pill>In Progress</Badge>
                              ) : (
                                <span className="text-muted" style={{ fontSize: '1rem' }}>N/A</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="text-muted" style={{ fontWeight: '500', fontSize: '0.85rem' }}>
                                  {test.testNumber ? `TEST-${String(test.testNumber).padStart(9, '0')}` : 'N/A'}
                                </span>
                                {test.testNumber && (
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="ml-1 p-0"
                                    style={{
                                      visibility: hoveredRowTestId === test.testId ? 'visible' : 'hidden'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const formattedTestCode = `TEST-${String(test.testNumber).padStart(9, '0')}`;
                                      navigator.clipboard.writeText(formattedTestCode);
                                      Swal.fire({
                                        icon: 'success',
                                        title: 'Copied!',
                                        text: `Test ID copied to clipboard`,
                                        timer: 1500,
                                        showConfirmButton: false
                                      });
                                    }}
                                    title="Copy Test ID"
                                  >
                                    <i className="fas fa-copy text-info" style={{ fontSize: '0.8rem' }}></i>
                                  </Button>
                                )}
                              </div>
                            </td>
                            <td>
                              {editingTestId === test.testId ? (
                                <div className="d-flex align-items-center">
                                  <Input
                                    type="text"
                                    value={editingTestName}
                                    onChange={(e) => setEditingTestName(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveTestName(test.testId);
                                      }
                                    }}
                                    autoFocus
                                    style={{ display: 'inline-block', width: 'auto', minWidth: '200px' }}
                                    bsSize="sm"
                                  />
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => handleSaveTestName(test.testId)}
                                    className="ml-2"
                                  >
                                    <i className="fas fa-check"></i>
                                  </Button>
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={handleCancelEditName}
                                    className="ml-1"
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </div>
                              ) : (
                                <div
                                  className="d-flex align-items-center"
                                  onClick={() => handleStartEditName(test)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click to edit test name"
                                >
                                  <i
                                    className="fas fa-edit text-primary mr-2"
                                    style={{
                                      fontSize: '0.875rem',
                                      visibility: hoveredRowTestId === test.testId ? 'visible' : 'hidden'
                                    }}
                                  ></i>
                                  <span
                                    className="font-weight-bold"
                                    style={{
                                      maxWidth: '180px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'inline-block'
                                    }}
                                    title={test.testName || 'Unnamed Test'}
                                  >
                                    {test.testName || 'Unnamed Test'}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>
                              <span>{formatDate(test.createdAt)}</span>
                            </td>
                            <td>
                              {test.status === 'completed' ? (
                                <span>
                                  {test.isTimed ? 'Timed' : 'Untimed'}, {test.isTutor ? 'Tutored' : 'Untutored'}
                                </span>
                              ) : (
                                <div
                                  onClick={() => handleOpenEditMode(test)}
                                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                                  title="Click to edit test mode"
                                >
                                  <span>
                                    {test.isTimed ? 'Timed' : 'Untimed'}, {test.isTutor ? 'Tutored' : 'Untutored'}
                                  </span>
                                  <i
                                    className="fas fa-edit text-primary ml-2"
                                    style={{
                                      fontSize: '0.75rem',
                                      visibility: hoveredRowTestId === test.testId ? 'visible' : 'hidden'
                                    }}
                                  ></i>
                                </div>
                              )}
                            </td>
                            <td>
                              <Badge color="primary" pill>
                                {test.selectionMode || 'Standard'}
                              </Badge>
                            </td>
                            <td>
                              {test.subjects && test.subjects.length > 0 ? (
                                test.subjects.length > 1 ? (
                                  <Badge color="success" pill title={test.subjects.join(', ')}>
                                    Multiple
                                  </Badge>
                                ) : (
                                  (() => {
                                    const subjectName = test.subjects[0];
                                    const subjectObj = subjects?.find(s => s.name === subjectName);
                                    const displayName = subjectObj
                                      ? subjectObj.name
                                      : subjectName;
                                    return (
                                      <Badge color="success" pill>
                                        {displayName}
                                      </Badge>
                                    );
                                  })()
                                )
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {test.units && test.units.length > 0 ? (
                                test.units.length > 1 ? (
                                  <Badge color="warning" pill title={test.units.join(', ')}>
                                    Multiple
                                  </Badge>
                                ) : (
                                  (() => {
                                    const unitName = test.units[0];
                                    const unitObj = units?.find(u => u.name === unitName);
                                    const displayName = unitObj
                                      ? unitObj.name
                                      : unitName;
                                    return (
                                      <Badge color="warning" pill>
                                        {displayName}
                                      </Badge>
                                    );
                                  })()
                                )
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {test.topics && test.topics.length > 0 ? (
                                test.topics.length > 1 ? (
                                  <Badge color="info" pill title={test.topics.join(', ')}>
                                    Multiple
                                  </Badge>
                                ) : (
                                  (() => {
                                    const topicName = test.topics[0];
                                    const topicObj = topics?.find(t => t.name === topicName);
                                    const displayName = topicObj
                                      ? topicObj.name
                                      : topicName;
                                    return (
                                      <Badge color="info" pill>
                                        {displayName}
                                      </Badge>
                                    );
                                  })()
                                )
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span className="font-weight-bold">{test.totalQuestions || 0}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
              {activeTab === 'tests' && !loading && tests && tests.length > 0 && sortedTests.length > 0 && (
                <CardFooter className="py-4">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                    <div className="text-muted small mb-2 mb-sm-0">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedTests.length)} of {sortedTests.length} tests
                      {searchQuery && ` (filtered from ${tests.length} total)`}
                    </div>
                    <div className="d-flex align-items-center">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || totalPages <= 1}
                        className="mr-2"
                      >
                        <i className="fas fa-chevron-left"></i>
                        <span className="d-none d-sm-inline ml-1">Previous</span>
                      </Button>
                      <span className="mx-2 text-nowrap">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages <= 1}
                        className="ml-2"
                      >
                        <span className="d-none d-sm-inline mr-1">Next</span>
                        <i className="fas fa-chevron-right"></i>
                      </Button>
                    </div>
                  </div>
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>

        {/* Edit Mode Modal */}
        <Modal isOpen={isEditModeModalOpen} toggle={handleCloseEditMode} size="sm" centered>
          <ModalHeader toggle={handleCloseEditMode}>
            Edit Test Mode
          </ModalHeader>
          <ModalBody>
            {editingModeTest && (
              <div>
                <div className="mb-4 text-center pb-3 border-bottom">
                  <strong>Test:</strong>
                  <p className="text-muted mb-0">{editingModeTest.testName || 'Unnamed Test'}</p>
                </div>

                <div className="mb-3">
                  <div className="d-flex align-items-center">
                    <span className="mr-3" style={{ minWidth: '80px' }}>
                      <strong>Tutor</strong>
                    </span>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="editTutor"
                        checked={editModeTutor}
                        onChange={(e) => setEditModeTutor(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="editTutor"></label>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Shows the correct answer and explanation after you answer each question
                  </small>
                </div>

                <div>
                  <div className="d-flex align-items-center">
                    <span className="mr-3" style={{ minWidth: '80px' }}>
                      <strong>Timed</strong>
                    </span>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="editTimed"
                        checked={editModeTimed}
                        onChange={(e) => setEditModeTimed(e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor="editTimed"></label>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Sets a time limit on the test
                  </small>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="justify-content-center">
            <Button color="secondary" onClick={handleCloseEditMode}>
              
              Cancel
            </Button>
            <Button color="primary" onClick={handleSaveTestMode}>
             
              Save
            </Button>
          </ModalFooter>
        </Modal>

        {/* Test Results Modal */}
        <Modal isOpen={isResultsModalOpen} toggle={() => setIsResultsModalOpen(false)} size="xl">
          <ModalHeader toggle={() => setIsResultsModalOpen(false)}>
            <i className="fas fa-chart-bar mr-2"></i>
            Test Results
            {selectedTest?.testName && (
              <span className="text-muted ml-2" style={{ fontSize: '0.9rem' }}>- {selectedTest.testName}</span>
            )}
          </ModalHeader>
          <ModalBody>
            {selectedTest && (
              <div>
                {/* Test Code and Question IDs Section */}
                <Row className="mb-3">
                  <Col>
                    <div className="d-flex flex-wrap align-items-center gap-3" style={{ gap: '1rem' }}>
                      <div className="d-flex align-items-center">
                        <span className="text-muted mr-2">Test ID:</span>
                        <Badge color="info" className="mr-2" style={{ fontSize: '0.85rem' }}>
                          {selectedTest.testId || `TEST-${String(selectedTest.testNumber).padStart(9, '0')}`}
                        </Badge>
                        <Button
                          color="link"
                          size="sm"
                          className="p-0"
                          onClick={() => {
                            const testCode = selectedTest.testId || `TEST-${String(selectedTest.testNumber).padStart(9, '0')}`;
                            navigator.clipboard.writeText(testCode);
                            Swal.fire({
                              icon: 'success',
                              title: 'Copied!',
                              text: 'Test code copied to clipboard',
                              timer: 1500,
                              showConfirmButton: false
                            });
                          }}
                          title="Copy test code"
                        >
                          <i className="fas fa-copy text-primary"></i>
                        </Button>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="text-muted mr-2">Question IDs:</span>
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => {
                            const questionIds = resultsData.map(r => r.questionNumber || r.questionId).join(',');
                            navigator.clipboard.writeText(questionIds);
                            Swal.fire({
                              icon: 'success',
                              title: 'Copied!',
                              text: `${resultsData.length} question IDs copied to clipboard`,
                              timer: 1500,
                              showConfirmButton: false
                            });
                          }}
                          title="Copy all question IDs"
                        >
                          <i className="fas fa-copy mr-1"></i>
                          Copy All ({resultsData.length})
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Test Settings Section */}
                <Row className="mb-4">
                  <Col md="6">
                    <Card className="bg-secondary shadow-sm mb-3 mb-md-0">
                      <CardBody className="py-3">
                        <h5 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                          Test Settings
                        </h5>
                        <Row>
                          <Col xs="6">
                            <p className="mb-1 text-sm text-muted">Mode</p>
                            <p className="font-weight-bold mb-0">{getModeDisplay(selectedTest)}</p>
                          </Col>
                          <Col xs="6">
                            <p className="mb-1 text-sm text-muted">Question Pool</p>
                            <p className="font-weight-bold mb-0">{getQuestionPoolDisplay(selectedTest)}</p>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Score Progress Section */}
                  <Col md="6">
                    <Card className="bg-secondary shadow-sm">
                      <CardBody className="py-3">
                        <h5 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                          Your Score
                        </h5>
                        <div className="d-flex align-items-center mb-2">
                          <span className="h2 mb-0 mr-3" style={{ color: (selectedTest.score || 0) >= 70 ? '#2dce89' : (selectedTest.score || 0) >= 50 ? '#fb6340' : '#f5365c' }}>
                            {selectedTest.score || 0}%
                          </span>
                          
                        </div>
                        <div className="position-relative" style={{ height: '20px' }}>
                          {/* Background bar */}
                          <Progress
                            value={100}
                            className="mb-0"
                            style={{ height: '20px', backgroundColor: '#e9ecef' }}
                          />
                          {/* User score bar */}
                          <div
                            className="position-absolute"
                            style={{
                              top: 0,
                              left: 0,
                              width: `${selectedTest.score || 0}%`,
                              height: '20px',
                              backgroundColor: (selectedTest.score || 0) >= 70 ? '#2dce89' : (selectedTest.score || 0) >= 50 ? '#fb6340' : '#f5365c',
                              borderRadius: '0.25rem',
                              transition: 'width 0.5s ease'
                            }}
                          />
                          {/* Average difficulty marker hidden for now */}
                        </div>
                        <div className="d-flex justify-content-between mt-1">
                          <small className="text-muted">0%</small>
                          <small className="text-muted">100%</small>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Results Table */}
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table className="align-items-center table-flush" hover>
                    <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th>Status</th>
                        <th>ID</th>
                        <th>Subject</th>
                        <th>Unit</th>
                        <th>Topic</th>
                        <th>% Correct Others</th>
                        <th>Time Spent</th>
                        <th title="Average time spent across all users (Timed + Untutored mode only)">
                          Avg Time Spent
                          <i className="fas fa-info-circle text-muted ml-1" style={{ fontSize: '0.7rem' }}></i>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultsData.map((result) => (
                        <tr key={result.questionId}>
                          <td>
                            {getResultStatusBadge(result.status)}
                          </td>
                          <td>
                            <span className="font-weight-bold">
                              {result.index} - {result.questionNumber || result.questionId?.slice(-6) || `Q${result.index}`}
                            </span>
                          </td>
                          <td>
                            <Badge color="primary" pill>
                              {result.subject}
                            </Badge>
                          </td>
                          <td>
                            <Badge color="info" pill>
                              {result.unit}
                            </Badge>
                          </td>
                          <td>
                            {result.topic && result.topic !== '-' ? (
                              <Badge color="secondary" pill>
                                {result.topic}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="mr-2">{result.percentCorrectOthers}%</span>
                              <Progress
                                value={result.percentCorrectOthers}
                                style={{ width: '60px', height: '6px' }}
                                color={result.percentCorrectOthers >= 70 ? 'success' : result.percentCorrectOthers >= 50 ? 'warning' : 'danger'}
                              />
                            </div>
                          </td>
                          <td>
                            <span className={result.timeSpent > result.averageTimeSpent ? 'text-warning' : 'text-success'}>
                              {formatTime(result.timeSpent)}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {result.averageTimeSpent > 0 ? formatTime(result.averageTimeSpent) : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {resultsData.length === 0 && (
                  <div className="text-center py-4">
                    <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No question data available for this test.</p>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="warning"
              onClick={() => {
                setIsResultsModalOpen(false);
                handleViewTestAnalysis(selectedTest);
              }}
            >
              <i className="fas fa-chart-pie mr-2"></i>
              View Analysis
            </Button>
            <Button
              color="info"
              onClick={() => {
                setIsResultsModalOpen(false);
                handleReviewTest(selectedTest);
              }}
            >
              <i className="fas fa-eye mr-2"></i>
              Review Test
            </Button>
            <Button color="secondary" onClick={() => setIsResultsModalOpen(false)}>
              <i className="fas fa-times mr-2"></i>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Test Analysis Modal */}
        <Modal isOpen={isAnalysisModalOpen} toggle={() => setIsAnalysisModalOpen(false)} size="xl">
          <ModalHeader toggle={() => setIsAnalysisModalOpen(false)}>
            <i className="fas fa-chart-pie mr-2"></i>
            Test Analysis
            {selectedTest?.testName && (
              <span className="text-muted ml-2" style={{ fontSize: '0.9rem' }}>- {selectedTest.testName}</span>
            )}
          </ModalHeader>
          <ModalBody>
            {analysisData && (
              <div>
                {/* Overall Performance Summary */}
                <Row className="mb-4">
                  <Col md="3" sm="6" className="mb-3 mb-md-0">
                    <Card className="bg-gradient-primary shadow-sm h-100">
                      <CardBody className="py-3 text-white">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <p className="text-white mb-1 small text-uppercase">Score</p>
                            <h2 className="mb-0 font-weight-bold text-white">{analysisData.overall.score}%</h2>
                          </div>
                          <div className="icon icon-shape bg-white text-primary rounded-circle shadow">
                            <i className="fas fa-percentage"></i>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3" sm="6" className="mb-3 mb-md-0">
                    <Card className="bg-gradient-success shadow-sm h-100">
                      <CardBody className="py-3 text-white">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <p className="text-white mb-1 small text-uppercase">Correct</p>
                            <h2 className="mb-0 font-weight-bold text-white">{analysisData.overall.correct}</h2>
                          </div>
                          <div className="icon icon-shape bg-white text-success rounded-circle shadow">
                            <i className="fas fa-check"></i>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3" sm="6" className="mb-3 mb-md-0">
                    <Card className="bg-gradient-danger shadow-sm h-100">
                      <CardBody className="py-3 text-white">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <p className="text-white mb-1 small text-uppercase">Incorrect</p>
                            <h2 className="mb-0 font-weight-bold text-white">{analysisData.overall.incorrect}</h2>
                          </div>
                          <div className="icon icon-shape bg-white text-danger rounded-circle shadow">
                            <i className="fas fa-times"></i>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3" sm="6">
                    <Card className="bg-gradient-warning shadow-sm h-100">
                      <CardBody className="py-3 text-white">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <p className="text-white mb-1 small text-uppercase">Omitted</p>
                            <h2 className="mb-0 font-weight-bold text-white">{analysisData.overall.omitted}</h2>
                          </div>
                          <div className="icon icon-shape bg-white text-warning rounded-circle shadow">
                            <i className="fas fa-minus"></i>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Strengths and Weaknesses */}
                <Row className="mb-4">
                  <Col md="6" className="mb-3 mb-md-0">
                    <Card className="shadow-sm h-100">
                      <CardHeader className="bg-transparent border-0 pb-0">
                        <h5 className="mb-0">
                          <i className="fas fa-trophy text-success mr-2"></i>
                          Strengths
                          <i className="fas fa-info-circle text-muted ml-2" id="strengthsInfoTest" style={{ fontSize: '0.75rem', cursor: 'pointer' }}></i>
                          <UncontrolledTooltip target="strengthsInfoTest" placement="top">
                            Topics scoring 70% or above
                          </UncontrolledTooltip>
                        </h5>
                        <small className="text-muted">Areas where you performed well</small>
                      </CardHeader>
                      <CardBody>
                        {analysisData.strengths.length > 0 ? (
                          <div>
                            {analysisData.strengths.map((item, index) => (
                              <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-success-light rounded" style={{ backgroundColor: 'rgba(45, 206, 137, 0.1)' }}>
                                <div>
                                  <span className="font-weight-bold">{item.topic || item.unit}</span>
                                  <br />
                                  <small className="text-muted">{item.topic ? item.unit : item.subject}</small>
                                </div>
                                <div className="text-right">
                                  <Badge color="success" className="px-3 py-2">
                                    {item.percentage}%
                                  </Badge>
                                  <br />
                                  <small className="text-muted">{item.correct}/{item.total} correct</small>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="fas fa-info-circle mr-2"></i>
                            Need more questions per topic to identify strengths
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6">
                    <Card className="shadow-sm h-100">
                      <CardHeader className="bg-transparent border-0 pb-0">
                        <h5 className="mb-0">
                          <i className="fas fa-exclamation-triangle text-danger mr-2"></i>
                          Areas for Improvement
                          <i className="fas fa-info-circle text-muted ml-2" id="weaknessesInfoTest" style={{ fontSize: '0.75rem', cursor: 'pointer' }}></i>
                          <UncontrolledTooltip target="weaknessesInfoTest" placement="top">
                            Topics scoring below 70%
                          </UncontrolledTooltip>
                        </h5>
                        <small className="text-muted">Focus your study on these topics</small>
                      </CardHeader>
                      <CardBody>
                        {analysisData.weaknesses.length > 0 ? (
                          <div>
                            {analysisData.weaknesses.map((item, index) => (
                              <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-danger-light rounded bg-danger" >
                                <div>
                                  <span className="font-weight-bold text-white">{item.topic || item.unit}</span>
                                  <br />
                                  <small className="text-lighter font-weight-bold">{item.topic ? item.unit : item.subject}</small>
                                </div>
                                <div className="text-right">
                                  <Badge color="secondary" className="bg-secondary px-3 py-2 text-danger">
                                    {item.percentage}%
                                  </Badge>
                                  <br />
                                  <small className="text-lighter ">{item.correct}/{item.total} correct</small>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="fas fa-check-circle text-success mr-2"></i>
                            Great job! No significant weaknesses identified
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Time Analysis */}
                <Row className="mb-4">
                  <Col md="12">
                    <Card className="shadow-sm">
                      <CardHeader className="bg-transparent border-0 pb-0">
                        <h5 className="mb-0">
                          <i className="fas fa-clock text-info mr-2"></i>
                          Time Analysis
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <Row>
                          <Col md="3" sm="6" className="text-center mb-3 mb-md-0">
                            <div className="p-3 bg-secondary rounded">
                              <i className="fas fa-hourglass-half text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                              <h4 className="mb-0">{formatTime(analysisData.timeAnalysis.totalTimeSpent)}</h4>
                              <small className="text-muted">Total Time</small>
                            </div>
                          </Col>
                          <Col md="3" sm="6" className="text-center mb-3 mb-md-0">
                            <div className="p-3 bg-secondary rounded">
                              <i className="fas fa-stopwatch text-info mb-2" style={{ fontSize: '1.5rem' }}></i>
                              <h4 className="mb-0">{formatTime(analysisData.timeAnalysis.avgTimePerQuestion)}</h4>
                              <small className="text-muted">Avg per Question</small>
                            </div>
                          </Col>
                          <Col md="3" sm="6" className="text-center mb-3 mb-md-0">
                            <div className="p-3 bg-secondary rounded">
                              <i className="fas fa-check-circle text-success mb-2" style={{ fontSize: '1.5rem' }}></i>
                              <h4 className="mb-0">{formatTime(analysisData.timeAnalysis.avgTimeCorrect)}</h4>
                              <small className="text-muted">Avg Time (Correct)</small>
                            </div>
                          </Col>
                          <Col md="3" sm="6" className="text-center">
                            <div className="p-3 bg-secondary rounded">
                              <i className="fas fa-times-circle text-danger mb-2" style={{ fontSize: '1.5rem' }}></i>
                              <h4 className="mb-0">{formatTime(analysisData.timeAnalysis.avgTimeIncorrect)}</h4>
                              <small className="text-muted">Avg Time (Incorrect)</small>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Difficulty Breakdown hidden for now */}

                {/* Performance by Subject */}
                <Row className="mb-4">
                  <Col md="12">
                    <Card className="shadow-sm">
                      <CardHeader className="bg-transparent border-0 pb-0">
                        <h5 className="mb-0">
                          <i className="fas fa-book text-primary mr-2"></i>
                          Performance by Subject
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <div className="table-responsive">
                          <Table className="align-items-center table-flush" size="sm">
                            <thead className="thead-light">
                              <tr>
                                <th>Subject</th>
                                <th>Total</th>
                                <th>Correct</th>
                                <th>Incorrect</th>
                                <th>Omitted</th>
                                <th>Score</th>
                                <th>Avg Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analysisData.subjectPerformance.map((subject, index) => (
                                <tr key={index}>
                                  <td>
                                    <Badge color="primary" pill>{subject.name}</Badge>
                                  </td>
                                  <td>{subject.total}</td>
                                  <td className="text-success font-weight-bold">{subject.correct}</td>
                                  <td className="text-danger font-weight-bold">{subject.incorrect}</td>
                                  <td className="text-warning">{subject.omitted}</td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <span className="mr-2">{subject.percentage}%</span>
                                      <Progress
                                        value={subject.percentage}
                                        style={{ width: '60px', height: '6px' }}
                                        color={subject.percentage >= 70 ? 'success' : subject.percentage >= 50 ? 'warning' : 'danger'}
                                      />
                                    </div>
                                  </td>
                                  <td>{formatTime(subject.avgTime)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Performance by Unit */}
                <Row>
                  <Col md="12">
                    <Card className="shadow-sm">
                      <CardHeader className="bg-transparent border-0 pb-0">
                        <h5 className="mb-0">
                          <i className="fas fa-folder-open text-info mr-2"></i>
                          Performance by Unit
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <Table className="align-items-center table-flush" size="sm">
                            <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th>Subject</th>
                                <th>Unit</th>
                                <th>Total</th>
                                <th>Correct</th>
                                <th>Incorrect</th>
                                <th>Score</th>
                                <th>Avg Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analysisData.unitPerformance.map((unit, index) => (
                                <tr key={index}>
                                  <td>
                                    <Badge color="primary" pill style={{ fontSize: '0.7rem' }}>{unit.subject}</Badge>
                                  </td>
                                  <td>
                                    <Badge color="info" pill style={{ fontSize: '0.7rem' }}>{unit.unit}</Badge>
                                  </td>
                                  <td>{unit.total}</td>
                                  <td className="text-success font-weight-bold">{unit.correct}</td>
                                  <td className="text-danger font-weight-bold">{unit.incorrect}</td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <span className="mr-2">{unit.percentage}%</span>
                                      <Progress
                                        value={unit.percentage}
                                        style={{ width: '50px', height: '6px' }}
                                        color={unit.percentage >= 70 ? 'success' : unit.percentage >= 50 ? 'warning' : 'danger'}
                                      />
                                    </div>
                                  </td>
                                  <td>{formatTime(unit.avgTime)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Performance by Topic */}
                {analysisData.topicPerformance && analysisData.topicPerformance.length > 0 && (
                  <Row>
                    <Col md="12">
                      <Card className="shadow-sm">
                        <CardHeader className="bg-transparent border-0 pb-0">
                          <h5 className="mb-0">
                            <i className="fas fa-tags text-secondary mr-2"></i>
                            Performance by Topic
                          </h5>
                        </CardHeader>
                        <CardBody>
                          <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <Table className="align-items-center table-flush" size="sm">
                              <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                  <th>Unit</th>
                                  <th>Topic</th>
                                  <th>Total</th>
                                  <th>Correct</th>
                                  <th>Incorrect</th>
                                  <th>Score</th>
                                  <th>Avg Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {analysisData.topicPerformance.map((topic, index) => (
                                  <tr key={index}>
                                    <td>
                                      <Badge color="info" pill style={{ fontSize: '0.7rem' }}>{topic.unit}</Badge>
                                    </td>
                                    <td>
                                      <Badge color="secondary" pill style={{ fontSize: '0.7rem' }}>{topic.topic}</Badge>
                                    </td>
                                    <td>{topic.total}</td>
                                    <td className="text-success font-weight-bold">{topic.correct}</td>
                                    <td className="text-danger font-weight-bold">{topic.incorrect}</td>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <span className="mr-2">{topic.percentage}%</span>
                                        <Progress
                                          value={topic.percentage}
                                          style={{ width: '50px', height: '6px' }}
                                          color={topic.percentage >= 70 ? 'success' : topic.percentage >= 50 ? 'warning' : 'danger'}
                                        />
                                      </div>
                                    </td>
                                    <td>{formatTime(topic.avgTime)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            )}

            {!analysisData && (
              <div className="text-center py-5">
                <i className="fas fa-chart-pie fa-3x text-muted mb-3"></i>
                <p className="text-muted">No analysis data available for this test.</p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                setIsAnalysisModalOpen(false);
                handleViewTestResults(selectedTest);
              }}
            >
              <i className="fas fa-chart-bar mr-2"></i>
              View Results
            </Button>
            <Button
              color="info"
              onClick={() => {
                setIsAnalysisModalOpen(false);
                handleReviewTest(selectedTest);
              }}
            >
              <i className="fas fa-eye mr-2"></i>
              Review Test
            </Button>
            <Button color="secondary" onClick={() => setIsAnalysisModalOpen(false)}>
              <i className="fas fa-times mr-2"></i>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
      </AccessRestriction>
    </>
  );
};

export default PreviousTests;
